import PropTypes from 'prop-types';
import { useMemo, useState, createContext, useContext, useCallback } from 'react';
// @mui
import { CssBaseline } from '@mui/material';
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
// hooks
import useSettings from '../hooks/useSettings';
//
import { getPalette, setPaletteFromAPI } from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';

// Create a context to provide both theme and update function
const EnhancedThemeContext = createContext({
  theme: null,
  updatePalette: () => { },
});

// Custom hook to get both the theme and update function
export const useEnhancedTheme = () => {
  const context = useContext(EnhancedThemeContext);
  if (!context) {
    throw new Error('useEnhancedTheme must be used within a ThemeProvider');
  }
  return context;
};

// Alternative hook for just the update function
export const useThemeUpdate = () => {
  const { updatePalette } = useEnhancedTheme();
  return { updatePalette };
};

// ----------------------------------------------------------------------

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
  const { themeMode, themeDirection } = useSettings();
  const isLight = themeMode === 'light';

  // State to store custom palette overrides
  const [customPalette, setCustomPalette] = useState({});

  // Function to update palette
  const updatePalette = useCallback((newColors) => {
    // Update the global palette function
    setPaletteFromAPI(newColors);
    // Also store locally to trigger re-render
    setCustomPalette(newColors);
  }, []);

  // Create theme with updated palette
  const theme = useMemo(() => {
    // Get base palette
    const basePalette = isLight ? getPalette().light : getPalette().dark;

    // Merge with custom palette overrides
    const mergedPalette = {
      ...basePalette,
      ...customPalette,
      // Deep merge for nested objects like primary, secondary, etc.
      ...(customPalette.primary && {
        primary: { ...basePalette.primary, ...customPalette.primary }
      }),
      ...(customPalette.secondary && {
        secondary: { ...basePalette.secondary, ...customPalette.secondary }
      }),
      ...(customPalette.error && {
        error: { ...basePalette.error, ...customPalette.error }
      }),
      ...(customPalette.warning && {
        warning: { ...basePalette.warning, ...customPalette.warning }
      }),
      ...(customPalette.info && {
        info: { ...basePalette.info, ...customPalette.info }
      }),
      ...(customPalette.success && {
        success: { ...basePalette.success, ...customPalette.success }
      }),
    };

    const themeOptions = {
      palette: mergedPalette,
      typography,
      breakpoints,
      shape: { borderRadius: 8 },
      direction: themeDirection,
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark,
    };

    const createdTheme = createTheme(themeOptions);
    createdTheme.components = componentsOverride(createdTheme);

    return createdTheme;
  }, [isLight, themeDirection, customPalette]);

  // Context value with both theme and update function
  const contextValue = useMemo(() => ({
    theme,
    updatePalette,
    // Expose commonly used theme properties for easy access
    palette: theme.palette,
    typography: theme.typography,
    breakpoints: theme.breakpoints,
    spacing: theme.spacing,
    shadows: theme.shadows,
    customShadows: theme.customShadows,
  }), [theme, updatePalette]);

  return (
    <StyledEngineProvider injectFirst>
      <EnhancedThemeContext.Provider value={contextValue}>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </MUIThemeProvider>
      </EnhancedThemeContext.Provider>
    </StyledEngineProvider>
  );
}